import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useAuth } from '@/auth/AuthProvider';
import { PageLoading } from '@/components/ui/States';
import { AppShell } from '@/layout/AppShell';
import { HomeRedirect } from '@/pages/common/HomeRedirect';
import { NotFoundPage } from '@/pages/common/NotFoundPage';
import { ConsentGate } from '@/components/domain/Privacy';
import { CvGate } from '@/components/domain/Cv';

const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const InvitePage = lazy(() => import('@/pages/auth/InvitePage'));
const WelcomePage = lazy(() => import('@/pages/common/WelcomePage'));
const NotificationsPage = lazy(() => import('@/pages/common/NotificationsPage'));
const MessagesPage = lazy(() => import('@/pages/common/MessagesPage'));
const TeamPanelPage = lazy(() => import('@/pages/team/TeamPanelPage'));
const TeamPanelIndex = lazy(() => import('@/pages/team/TeamPanelPage').then((m) => ({ default: m.TeamPanelIndex })));
const MyDisputesPage = lazy(() => import('@/pages/common/DisputesPages').then((m) => ({ default: m.MyDisputesPage })));
const OrgDisputesPage = lazy(() => import('@/pages/common/DisputesPages').then((m) => ({ default: m.OrgDisputesPage })));
const AdminDisputesPage = lazy(() => import('@/pages/common/DisputesPages').then((m) => ({ default: m.AdminDisputesPage })));
const NewOrgPage = lazy(() => import('@/pages/customer/NewOrgPage'));
const OrgSettingsPage = lazy(() => import('@/pages/customer/OrgSettingsPage'));
const ProjectsPage = lazy(() => import('@/pages/customer/ProjectsPage'));
const ProjectLayout = lazy(() => import('@/pages/customer/ProjectLayout'));
const ProjectOverviewPage = lazy(() => import('@/pages/customer/ProjectOverviewPage'));
const ReleaseQualityPage = lazy(() => import('@/pages/customer/ReleaseQualityPage'));
const RhythmPage = lazy(() => import('@/pages/customer/RhythmPage'));
const CasesPage = lazy(() => import('@/pages/customer/CasesPage'));
const CaseEditorPage = lazy(() => import('@/pages/customer/CaseEditorPage'));
const KnownIssuesPage = lazy(() => import('@/pages/customer/KnownIssuesPage'));
const CyclesPage = lazy(() => import('@/pages/customer/CyclesPage'));
const CycleWizardPage = lazy(() => import('@/pages/customer/CycleWizardPage'));
const CycleBoardPage = lazy(() => import('@/pages/customer/CycleBoardPage'));
const ReviewQueuePage = lazy(() => import('@/pages/customer/ReviewQueuePage'));
const ReviewPage = lazy(() => import('@/pages/customer/review/ReviewPage'));
const BugsPage = lazy(() => import('@/pages/customer/BugsPage'));
const BugDetailPage = lazy(() => import('@/pages/customer/BugDetailPage'));
const ProposalsPage = lazy(() => import('@/pages/customer/ProposalsPage'));
const ProjectSettingsPage = lazy(() => import('@/pages/customer/ProjectSettingsPage'));
const CommunitiesPage = lazy(() => import('@/pages/lead/CommunitiesPage'));
const CommunityPage = lazy(() => import('@/pages/lead/CommunityPage'));
const MyAssignmentsPage = lazy(() => import('@/pages/tester/MyAssignmentsPage'));
const RunnerPage = lazy(() => import('@/pages/tester/runner/RunnerPage'));
const ExploratoryNewPage = lazy(() => import('@/pages/tester/ExploratoryNewPage'));
const MyBugsPage = lazy(() => import('@/pages/tester/MyBugsPage'));
const MyReputationPage = lazy(() => import('@/pages/tester/MyReputationPage'));
const DevicesPage = lazy(() => import('@/pages/tester/DevicesPage'));
const OrgLogsPage = lazy(() => import('@/pages/logs/LogPages').then((m) => ({ default: m.OrgLogsPage })));
const ProjectLogsPage = lazy(() => import('@/pages/logs/LogPages').then((m) => ({ default: m.ProjectLogsPage })));
const MyActivityPage = lazy(() => import('@/pages/logs/LogPages').then((m) => ({ default: m.MyActivityPage })));
const AdminLogsPage = lazy(() => import('@/pages/logs/LogPages').then((m) => ({ default: m.AdminLogsPage })));
const JobsPage = lazy(() => import('@/pages/market/JobsPage'));
const JobDetailPage = lazy(() => import('@/pages/market/JobDetailPage'));
const MyProfilePage = lazy(() => import('@/pages/market/MyProfilePage'));
const MyCvPage = lazy(() => import('@/pages/tester/MyCvPage'));
const MyEarningsPage = lazy(() => import('@/pages/market/MyEarningsPage'));
const TeamsPage = lazy(() => import('@/pages/market/DirectoryPages').then((m) => ({ default: m.TeamsPage })));
const TeamPublicPage = lazy(() => import('@/pages/market/DirectoryPages').then((m) => ({ default: m.TeamPublicPage })));
const TesterPublicPage = lazy(() => import('@/pages/market/DirectoryPages').then((m) => ({ default: m.TesterPublicPage })));
const TalentPage = lazy(() => import('@/pages/market/DirectoryPages').then((m) => ({ default: m.TalentPage })));
const BillingPage = lazy(() => import('@/pages/billing/BillingPages').then((m) => ({ default: m.BillingPage })));
const StatementPage = lazy(() => import('@/pages/billing/BillingPages').then((m) => ({ default: m.StatementPage })));
const AdminBillingPage = lazy(() => import('@/pages/billing/BillingPages').then((m) => ({ default: m.AdminBillingPage })));
const StatementDocumentPage = lazy(() => import('@/pages/billing/DocumentPages').then((m) => ({ default: m.StatementDocumentPage })));
const OrgMembershipPage = lazy(() => import('@/pages/billing/MembershipPages').then((m) => ({ default: m.OrgMembershipPage })));
const MyMembershipPage = lazy(() => import('@/pages/billing/MembershipPages').then((m) => ({ default: m.MyMembershipPage })));
const AdminMembershipsPage = lazy(() => import('@/pages/admin/AdminMembershipsPage').then((m) => ({ default: m.AdminMembershipsPage })));
const InvoiceDocumentPage = lazy(() => import('@/pages/billing/DocumentPages').then((m) => ({ default: m.InvoiceDocumentPage })));
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminPages').then((m) => ({ default: m.AdminDashboardPage })));
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminPages').then((m) => ({ default: m.AdminUsersPage })));
const AdminUserPage = lazy(() => import('@/pages/admin/AdminPages').then((m) => ({ default: m.AdminUserPage })));
const AdminFraudPage = lazy(() => import('@/pages/admin/AdminPages').then((m) => ({ default: m.AdminFraudPage })));
const AdminErrorsPage = lazy(() => import('@/pages/admin/ErrorsPage').then((m) => ({ default: m.AdminErrorsPage })));
const PrivacyPage = lazy(() => import('@/pages/common/PrivacyPage'));
const CycleReportPage = lazy(() => import('@/pages/customer/CycleReportPage'));
const PhonePage = lazy(() => import('@/pages/phone/PhonePage'));
const AnnualEarningsPage = lazy(() => import('@/pages/billing/DocumentPages').then((m) => ({ default: m.AnnualEarningsPage })));

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <PageLoading />;
  if (status === 'anonymous') {
    const next = location.pathname + location.search + location.hash;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  // Anyone signed in must have seen the current privacy notice first; testers then fill in their CV once.
  return <>{children}<ConsentGate /><CvGate /></>;
}

export function App() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/invite/:token" element={<InvitePage />} />
        {/* Opened on the tester's phone from the QR code on the test screen: the code is the permission, no sign-in. */}
        <Route path="/m/:code" element={<PhonePage />} />

        {/* The runner is a focused, narrow layout meant to sit next to the app under test. */}
        <Route path="/run/:assignmentId" element={<RequireAuth><RunnerPage /></RequireAuth>} />
        <Route path="/executions/:executionId/run" element={<RequireAuth><RunnerPage /></RequireAuth>} />
        <Route path="/print/statements/:statementId" element={<RequireAuth><StatementDocumentPage /></RequireAuth>} />
        <Route path="/print/invoices/:invoiceId" element={<RequireAuth><InvoiceDocumentPage /></RequireAuth>} />
        <Route path="/print/earnings" element={<RequireAuth><AnnualEarningsPage /></RequireAuth>} />
        <Route path="/print/cycles/:cycleId" element={<RequireAuth><CycleReportPage /></RequireAuth>} />

        <Route element={<RequireAuth><AppShell /></RequireAuth>}>
          <Route index element={<HomeRedirect />} />
          <Route path="welcome" element={<WelcomePage />} />

          <Route path="org/new" element={<NewOrgPage />} />
          <Route path="org/settings" element={<OrgSettingsPage />} />
          <Route path="org/logs" element={<OrgLogsPage />} />
          <Route path="org/billing" element={<BillingPage />} />
          <Route path="org/membership" element={<OrgMembershipPage />} />
          <Route path="org/billing/statements/:statementId" element={<StatementPage />} />
          <Route path="talent" element={<TalentPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="projects/:projectId" element={<ProjectLayout />}>
            <Route index element={<ProjectOverviewPage />} />
            <Route path="cases" element={<CasesPage />} />
            <Route path="cases/new" element={<CaseEditorPage />} />
            <Route path="cases/:caseId" element={<CaseEditorPage />} />
            <Route path="known-issues" element={<KnownIssuesPage />} />
            <Route path="cycles" element={<CyclesPage />} />
            <Route path="cycles/new" element={<CycleWizardPage />} />
            <Route path="cycles/:cycleId/edit" element={<CycleWizardPage />} />
            <Route path="review" element={<ReviewQueuePage />} />
            <Route path="bugs" element={<BugsPage />} />
            <Route path="quality" element={<ReleaseQualityPage />} />
            <Route path="rhythm" element={<RhythmPage />} />
            <Route path="proposals" element={<ProposalsPage />} />
            <Route path="settings" element={<ProjectSettingsPage />} />
            <Route path="logs" element={<ProjectLogsPage />} />
          </Route>
          <Route path="cycles/:cycleId" element={<CycleBoardPage />} />
          <Route path="executions/:executionId/review" element={<ReviewPage />} />
          <Route path="bugs/:bugId" element={<BugDetailPage />} />

          <Route path="communities" element={<CommunitiesPage />} />
          <Route path="communities/:communityId" element={<CommunityPage />} />
          <Route path="team-panel" element={<TeamPanelIndex />} />
          <Route path="team-panel/:teamId" element={<TeamPanelPage />} />

          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="messages" element={<MessagesPage />} />
          <Route path="my/disputes" element={<MyDisputesPage />} />
          <Route path="org/disputes" element={<OrgDisputesPage />} />
          <Route path="admin/disputes" element={<AdminDisputesPage />} />
          <Route path="my/assignments" element={<MyAssignmentsPage />} />
          <Route path="my/exploratory/new" element={<ExploratoryNewPage />} />
          <Route path="my/bugs" element={<MyBugsPage />} />
          <Route path="my/reputation" element={<MyReputationPage />} />
          <Route path="my/devices" element={<DevicesPage />} />
          <Route path="my/activity" element={<MyActivityPage />} />
          <Route path="my/profile" element={<MyProfilePage />} />
          <Route path="my/cv" element={<MyCvPage />} />
          <Route path="my/earnings" element={<MyEarningsPage />} />
          <Route path="my/membership" element={<MyMembershipPage />} />
          <Route path="jobs" element={<JobsPage />} />
          <Route path="jobs/:cycleId" element={<JobDetailPage />} />
          <Route path="teams" element={<TeamsPage />} />
          <Route path="teams/:teamId" element={<TeamPublicPage />} />
          <Route path="testers/:userId" element={<TesterPublicPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="admin" element={<AdminDashboardPage />} />
          <Route path="admin/users" element={<AdminUsersPage />} />
          <Route path="admin/users/:userId" element={<AdminUserPage />} />
          <Route path="admin/fraud" element={<AdminFraudPage />} />
          <Route path="admin/logs" element={<AdminLogsPage />} />
          <Route path="admin/billing" element={<AdminBillingPage />} />
          <Route path="admin/memberships" element={<AdminMembershipsPage />} />
          <Route path="admin/errors" element={<AdminErrorsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
